import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { ApiError, unwrap } from '../api/api-error';
import type { components } from '../api/schema';

export type Workspace = components['schemas']['Workspace'];
export type WorkspaceItem = components['schemas']['WorkspaceItem'];
export type WorkspacePermission = Workspace['permissions'][number];
export type Member = components['schemas']['Member'];
export type PendingInvitation = components['schemas']['PendingInvitation'];
export type Invitation = components['schemas']['Invitation'];
export type AuditEntry = components['schemas']['AuditEntry'];
export type AuditAction = AuditEntry['action'];
export type WorkspaceRole = Member['role'];

export const WORKSPACES_KEY = ['workspaces'] as const;
export const INBOX_KEY = ['invitations', 'inbox'] as const;
export const workspaceKey = (workspaceId: string) => ['workspaces', workspaceId] as const;

/** Мои пространства постранично: «показать ещё» догружает следующую страницу по курсору. */
export function useMyWorkspaces() {
  return useInfiniteQuery({
    queryKey: [...WORKSPACES_KEY, 'list'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/workspaces', {
          params: { query: pageParam === undefined ? {} : { cursor: pageParam } },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}

/** Пространство с моими правами; `null` — нет доступа (404: чужое, архив, удалили из него). */
export function useWorkspace(workspaceId: string) {
  return useQuery({
    queryKey: workspaceKey(workspaceId),
    queryFn: async (): Promise<Workspace | null> => {
      try {
        return unwrap(
          await api.GET('/api/v1/workspaces/{workspaceId}', {
            params: { path: { workspaceId } },
          }),
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

/** Сколько раз спросить о личном пространстве, прежде чем сказать, что оно не готово. */
export const LAST_WORKSPACE_ATTEMPTS = 30;
export const LAST_WORKSPACE_KEY = [...WORKSPACES_KEY, 'last'] as const;

/**
 * Куда вести после входа. Личное пространство создаётся по факту активации (ADR-0014) —
 * сразу после первого входа его может ещё не быть: спрашиваем снова раз в секунду, но не
 * бесконечно (факт мог застрять в очереди).
 */
export function useLastWorkspace() {
  return useQuery({
    queryKey: LAST_WORKSPACE_KEY,
    queryFn: async () => unwrap(await api.GET('/api/v1/workspaces/last')),
    refetchInterval: (query) =>
      query.state.data?.workspace === null && query.state.dataUpdateCount < LAST_WORKSPACE_ATTEMPTS
        ? 1_000
        : false,
  });
}

/** «Входящие»: приглашения на мой подтверждённый email. */
export function useInbox() {
  return useQuery({
    queryKey: INBOX_KEY,
    queryFn: async () => unwrap(await api.GET('/api/v1/invitations')),
  });
}

export function can(workspace: Workspace | null | undefined, permission: WorkspacePermission) {
  return workspace?.permissions.includes(permission) === true;
}
